import React, { useCallback } from 'react';

// DEPENDENCIES
import snakeCase from 'lodash/snakeCase';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE } from 'pathnames.js';

// HELPERS/FUNCTIONS
import { apiFetch, documentFetch, fileDownload, makePath, modalFunctions, sleep } from 'functions.js';
import { recountActions } from 'actions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Sprite from 'core/tools/Sprite';

// MAIN COMPONENT
const AMFinanceButton = ({ customerName, stockNum }) => {
  const { recordID } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const handlePrint = useCallback(() => {
    const args = {
      endpoint: makePath(ENDPOINTS.sales.print.recap, recordID),
      params: {
        recapType: 'Gross',
        printCleanUpForm: 1
      },
      accept: 'pdf',
      contentType: 'pdf',
      mimeType: 'application/pdf',
      loadingMessage: 'Downloading Gross Recap and Clean-Up Form',
      errorMessage: 'Unable to download Gross Recap and Clean-Up Form',
      successMessage: 'Gross Recap and Clean-Up Form downloaded.',
      onSuccess: ({ url }) => {
        fileDownload(url, `${stockNum}_${snakeCase(customerName)}_gross_recap_cleanup`, undefined, true, true);
      }
    };
    documentFetch(args);
  }, [customerName, recordID, stockNum]);

  const renderConfirmationModal = useCallback(() => {
    modalFunctions.add({
      type: 'confirmation',
      title: 'Sale Confirmation',
      body: (
        <div className="d-flex">
          <Sprite use="check-circle" fill="success" className="me-2" />
          <span className="flex-grow-1">This sale has been successfully pushed to AM/Finance.</span>
        </div>
      ),
      cancelButton: {
        col: { xs: { span: 24, order: 2 }, lg: { span: 8, order: 1 } }
      },
      continueButton: {
        label: 'Print Gross Recap and Clean-Up Form',
        icon: {
          use: 'print'
        },
        onClick: handlePrint,
        col: { xs: { span: 24, order: 1 }, lg: { span: 16, order: 2 } },
        className: 'mb-4 mb-lg-0'
      },
      spacing: 'sm'
    });
  }, [handlePrint]);

  const handleSave = useCallback(() => {
    apiFetch({
      method: 'PUT',
      endpoint: ENDPOINTS.amFinance.status(recordID),
      loadingMessage: 'Submitting AM/Finance',
      errorMessage: 'Unable to submit AM/Finance',
      successMessage: 'Sale status has been changed to AM/Finance.',
      onSuccess: async () => {
        renderConfirmationModal();

        await sleep(1000);
        dispatch(recountActions.toggle());

        navigate(makePath(INTERFACE.amFinance), {
          state: {
            statusMessage: 'Sale has been pushed back to AM/Finance.'
          }
        });
      }
    });
  }, [dispatch, recordID, renderConfirmationModal, navigate]);

  return <Button onClick={handleSave} label="AM/Finance" outline fullWidth={false} size="sm" />;
};

// EXPORT
export default AMFinanceButton;
